// Unified input: WASD/arrows + a single ACTION key (Space) on desktop,
// plus an on-screen joystick + ACTION button on touch. The action is
// context-sensitive — the scene decides whether a press patches or dashes.
// The scene can call setActionLabel('PATCH'|'ACT'|...) to change the
// button caption when context changes.

import Phaser from 'phaser';
import { brandHex } from '../brand';

export interface InputAccess {
  axisX(): number;
  axisY(): number;
  actionPressed(): boolean;
  setActionLabel(label: string, color?: number): void;
  destroy(): void;
}

export const createInput = (scene: Phaser.Scene): InputAccess => {
  const cursors = scene.input.keyboard?.createCursorKeys();
  const wasd = scene.input.keyboard?.addKeys({
    W: Phaser.Input.Keyboard.KeyCodes.W,
    A: Phaser.Input.Keyboard.KeyCodes.A,
    S: Phaser.Input.Keyboard.KeyCodes.S,
    D: Phaser.Input.Keyboard.KeyCodes.D,
  }) as Record<'W' | 'A' | 'S' | 'D', Phaser.Input.Keyboard.Key> | undefined;
  const spaceKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
  const eKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.E);

  let actionEdge = false;
  spaceKey?.on('down', () => { actionEdge = true; });
  eKey?.on('down', () => { actionEdge = true; });

  const isTouch = scene.sys.game.device.input.touch || window.matchMedia('(pointer: coarse)').matches;

  // --- HTML joystick elements ---
  const joyBase = document.getElementById('ix-joy-base')!;
  const joyStick = document.getElementById('ix-joy-stick')!;
  const actBtn = document.getElementById('ix-act-btn')!;

  if (isTouch) {
    joyBase.classList.remove('hidden');
    actBtn.classList.remove('hidden');
  }

  // AbortController for the DOM listener so it doesn't leak across replays.
  const ac = new AbortController();

  // ACT button press
  actBtn.addEventListener('pointerdown', () => {
    actionEdge = true;
    actBtn.style.transform = 'scale(0.88)';
    setTimeout(() => { actBtn.style.transform = ''; }, 160);
  }, { signal: ac.signal });

  // Joystick state
  let joyAxisX = 0, joyAxisY = 0;
  let joyBaseX = 0, joyBaseY = 0;
  let joyActive = false;
  let joyPointerId = -1;

  const joyRadius = 55;

  // The joystick base is fixed bottom-left via CSS; we only reposition the inner stick.
  // For a floating joystick (touch starts anywhere in lower-left), we update joyBaseX/Y
  // to match where the finger lands, but keep the visual base centered via CSS unless
  // the touch is within the base bounds.

  scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
    if (!isTouch) return;
    if (p.x < scene.scale.width / 2 && p.y > scene.scale.height * 0.5) {
      joyActive = true;
      joyPointerId = p.id;
      joyBaseX = p.x;
      joyBaseY = p.y;
      // Reset stick to center
      joyStick.style.transform = 'translate(-50%, -50%)';
    }
  });

  scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
    if (!joyActive || p.id !== joyPointerId) return;
    const dx = p.x - joyBaseX;
    const dy = p.y - joyBaseY;
    const d = Math.min(Math.hypot(dx, dy), joyRadius);
    const ang = Math.atan2(dy, dx);
    const sx = Math.cos(ang) * d;
    const sy = Math.sin(ang) * d;
    joyAxisX = sx / joyRadius;
    joyAxisY = sy / joyRadius;
    // Move stick visually — offset from its CSS center position
    joyStick.style.transform = `translate(calc(-50% + ${sx}px), calc(-50% + ${sy}px))`;
  });

  const releaseJoy = (p: Phaser.Input.Pointer) => {
    if (p.id !== joyPointerId) return;
    joyActive = false;
    joyAxisX = 0;
    joyAxisY = 0;
    joyStick.style.transform = 'translate(-50%, -50%)';
  };
  scene.input.on('pointerup', releaseJoy);
  scene.input.on('pointerupoutside', releaseJoy);

  return {
    axisX(): number {
      let x = 0;
      if (cursors?.left.isDown || wasd?.A.isDown) x -= 1;
      if (cursors?.right.isDown || wasd?.D.isDown) x += 1;
      if (joyActive) x += joyAxisX;
      return Phaser.Math.Clamp(x, -1, 1);
    },
    axisY(): number {
      let y = 0;
      if (cursors?.up.isDown || wasd?.W.isDown) y -= 1;
      if (cursors?.down.isDown || wasd?.S.isDown) y += 1;
      if (joyActive) y += joyAxisY;
      return Phaser.Math.Clamp(y, -1, 1);
    },
    actionPressed(): boolean {
      const v = actionEdge;
      actionEdge = false;
      return v;
    },
    setActionLabel(label: string, color: number = brandHex.primary) {
      actBtn.textContent = label;
      const hex = `#${color.toString(16).padStart(6, '0')}`;
      actBtn.style.background = hex;
      // Use alt class for non-primary colours (yellow/asset colour = bright, needs dark text)
      const isDark = color === brandHex.primary || color === brandHex.threat || color === brandHex.bgDeep;
      actBtn.style.color = isDark ? '#fff' : '#0E204F';
      actBtn.style.boxShadow = isDark ? `0 4px 0 #0E204F` : `0 4px 0 rgba(14,32,79,0.4)`;
    },
    destroy() {
      ac.abort();
      joyBase.classList.add('hidden');
      actBtn.classList.add('hidden');
      joyStick.style.transform = 'translate(-50%, -50%)';
      joyAxisX = 0;
      joyAxisY = 0;
    },
  };
};
